/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useMemo } from 'react';
import { useLocalStorage } from '../hooks/useLocalStorage';

export interface VisualEffectsSettings {
  particles: boolean;
  glowEffects: boolean;
  enhancedAnimations: boolean;
}

interface VisualEffectsContextValue {
  settings: VisualEffectsSettings;
  setSetting: <K extends keyof VisualEffectsSettings>(key: K, value: VisualEffectsSettings[K]) => void;
}

const VisualEffectsContext = createContext<VisualEffectsContextValue | undefined>(undefined);

const DEFAULT_SETTINGS: VisualEffectsSettings = {
  particles: true,
  glowEffects: true,
  enhancedAnimations: true,
};

interface VisualEffectsProviderProps {
  children: React.ReactNode;
}

export function VisualEffectsProvider({ children }: VisualEffectsProviderProps) {
  const [settings, setSettings] = useLocalStorage<VisualEffectsSettings>(
    'healfm-visual-effects',
    DEFAULT_SETTINGS
  );

  const setSetting = useCallback(<K extends keyof VisualEffectsSettings>(
    key: K,
    value: VisualEffectsSettings[K],
  ) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  }, [setSettings]);

  const contextValue = useMemo<VisualEffectsContextValue>(
    () => ({ settings, setSetting }),
    [settings, setSetting]
  );

  return (
    <VisualEffectsContext.Provider value={contextValue}>{children}</VisualEffectsContext.Provider>
  );
}

export function useVisualEffects(): VisualEffectsContextValue {
  const context = useContext(VisualEffectsContext);
  if (context === undefined) {
    throw new Error('useVisualEffects must be used within a VisualEffectsProvider');
  }
  return context;
}
